import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, FormikProvider, useFormik } from 'formik';
import { toast } from 'react-toastify';

import { ISponsor } from 'src/@types/leaderboard';
import { ModalProps } from 'src/layouts/AdminLayout';
import sponsorConroller from 'src/services/leaderboard/sponsorConroller';
import Button from 'src/ui/Button';
import Input from 'src/ui/Input';
import Modal from 'src/ui/Modal';

import { FORM_VALIDATION_SCHEMA, INITIAL_VALUES } from './constants';

import s from './style.module.scss';

import LogoImage from 'src/assets/images/btcs-logo.png';

export type GiveawayModalProps = {
  sponsorId: string;
  editingPrize?: any | null;
  onSuccessCreated?: (raffle: ISponsor) => void;
} & ModalProps;

interface IFormState {
  prize: number;
}

const clearFormState = {
  prize: 0,
};

function AddSponsorPrize({
  editingPrize,
  sponsorId,
  onSuccessCreated,
  onClose,
  open,
}: GiveawayModalProps) {
  const queryClient = useQueryClient();
  const isEdit = !!editingPrize;
  const [error, setError] = useState<string | null>(null);

  const [formState, setFormState] = useState<IFormState>(clearFormState);

  const formikProps = {
    initialValues: INITIAL_VALUES,
    validationSchema: FORM_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      if (isLoadingCreate || isLoadingEdit) {
        return;
      }

      if (isEdit) {
        onEdit();
      } else {
        onCreate();
      }
    },
  };

  const formik = useFormik(formikProps);

  // Create sponsor
  const { mutate: create, isPending: isLoadingCreate } = useMutation({
    mutationFn: (body: any) =>
      sponsorConroller.addSponsorPrize({ sponsorId, body }),
    onSuccess: (data) => {
      if (onSuccessCreated) onSuccessCreated(data);
      queryClient.invalidateQueries({
        queryKey: ['sponsor-prize', sponsorId],
      });
      handleClose();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || 'Error adding prize';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Add prize error:', error);
    },
  });

  const { mutate: edit, isPending: isLoadingEdit } = useMutation({
    mutationFn: (body: any) =>
      sponsorConroller.updateSponsorPrize({
        sponsorId,
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sponsor-prize', sponsorId],
      });
      toast.success('Sponsor edited successfully');
      handleClose();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || 'Error sponsor raffle';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Edit sponsor error:', error);
    },
  });

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const formBody = () => {
    const { prize } = formState;

    const body = {
      sponsorId: sponsorId,
      prize: Number(prize),
    };

    return body;
  };

  const onCreate = () => {
    const body = formBody();

    create(body);
  };

  const onEdit = () => {
    const body = formBody();
    //@ts-ignore
    body.id = editingPrize.id;

    edit(body);
  };

  const onCancel = () => {
    onClose();
    setFormState(clearFormState);
  };

  useEffect(() => {
    if (editingPrize) {
      setFormState({
        prize: editingPrize.prize || 0,
      });

      formik.setValues({
        prize: editingPrize.prize || 0,
      });
    }
  }, [editingPrize]);

  return (
    <Modal maxWidth="sm" open={open} handleClose={handleClose} isCloseIcon>
      <FormikProvider value={formik}>
        <Form>
          <div className={s.modal__header}>
            <img src={LogoImage} alt="logo" />

            <h2>
              {isEdit ? `Edit ` : `Add `}
              Prize
            </h2>
          </div>

          <div className={s.modal__form}>
            <Input
              label="Prize amount"
              name="prize"
              type="number"
              placeholder="Type here"
              value={formState.prize}
              onChange={(e) => {
                formik.setFieldValue(e.target.name, e.target.value);
                handleChange(e);
              }}
              error={formik.errors.prize}
            />
          </div>

          <div className={s.modal__footer}>
            {error && <div className={s.modal__error}>{error}</div>}

            <div className={s.modal__row}>
              <Button
                onClick={onCancel}
                className={s.modal__btn}
                isOutlinedWhite
              >
                Cancel
              </Button>
              <Button className={s.modal__btn} type="submit">
                {isLoadingCreate || isLoadingEdit
                  ? 'Submitting...'
                  : isEdit
                    ? `Edit`
                    : `Add`}
              </Button>
            </div>
          </div>
        </Form>
      </FormikProvider>
    </Modal>
  );
}

export default AddSponsorPrize;
